/**
 * react-headless-timeline v0.2.2
 * Copyright Dawid Zajac <zajdawid@protonmail.com>
 */

(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('react')) :
  typeof define === 'function' && define.amd ? define(['exports', 'react'], factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global["react-headless-timeline"] = {}, global.React));
})(this, (function (exports, React) { 'use strict';

  function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

  var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

  var DEFAULT_TIMELINE_DIRECTION = 'horizontal';

  var TimelineCtx = React.createContext(undefined);
  function TimelineProvider(_a) {
      var children = _a.children, startDate = _a.startDate, endDate = _a.endDate, _b = _a.direction, direction = _b === void 0 ? DEFAULT_TIMELINE_DIRECTION : _b;
      var value = {
          startDate: startDate,
          endDate: endDate,
          direction: direction,
      };
      return React__default["default"].createElement(TimelineCtx.Provider, { value: value }, children);
  }

  /*! *****************************************************************************
  Copyright (c) Microsoft Corporation.

  Permission to use, copy, modify, and/or distribute this software for any
  purpose with or without fee is hereby granted.

  THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
  REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
  AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
  INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
  LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
  OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
  PERFORMANCE OF THIS SOFTWARE.
  ***************************************************************************** */
  /* global Reflect, Promise */

  var extendStatics = function(d, b) {
      extendStatics = Object.setPrototypeOf ||
          ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
          function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
      return extendStatics(d, b);
  };

  function __extends(d, b) {
      extendStatics(d, b);
      function __() { this.constructor = d; }
      d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  }

  var __assign = function() {
      __assign = Object.assign || function __assign(t) {
          for (var s, i = 1, n = arguments.length; i < n; i++) {
              s = arguments[i];
              for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
          }
          return t;
      };
      return __assign.apply(this, arguments);
  };

  function toInteger(dirtyNumber) {
    if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
      return NaN;
    }

    var number = Number(dirtyNumber);

    if (isNaN(number)) {
      return number;
    }

    return number < 0 ? Math.ceil(number) : Math.floor(number);
  }

  function requiredArgs(required, args) {
    if (args.length < required) {
      throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
    }
  }

  /**
   * @name toDate
   * @category Common Helpers
   * @summary Convert the given argument to an instance of Date.
   *
   * @description
   * Convert the given argument to an instance of Date.
   *
   * If the argument is an instance of Date, the function returns its clone.
   *
   * If the argument is a number, it is treated as a timestamp.
   *
   * If the argument is none of the above, the function returns Invalid Date.
   *
   * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
   *
   * @param {Date|Number} argument - the value to convert
   * @returns {Date} the parsed date in the local time zone
   * @throws {TypeError} 1 argument required
   *
   * @example
   * // Clone the date:
   * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
   * //=> Tue Feb 11 2014 11:30:30
   *
   * @example
   * // Convert the timestamp to date:
   * const result = toDate(1392098430000)
   * //=> Tue Feb 11 2014 11:30:30
   */

  function toDate(argument) {
    requiredArgs(1, arguments);
    var argStr = Object.prototype.toString.call(argument); // Clone the date

    if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
      // Prevent the date to lose the milliseconds when passed to new Date() in IE10
      return new Date(argument.getTime());
    } else if (typeof argument === 'number' || argStr === '[object Number]') {
      return new Date(argument);
    } else {
      if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments"); // eslint-disable-next-line no-console

        console.warn(new Error().stack);
      }

      return new Date(NaN);
    }
  }

  /**
   * @name addDays
   * @category Day Helpers
   * @summary Add the specified number of days to the given date.
   *
   * @description
   * Add the specified number of days to the given date.
   *
   * @param {Date|Number} date - the date to be changed
   * @param {Number} amount - the amount of days to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
   * @returns {Date} - the new date with the days added
   * @throws {TypeError} - 2 arguments required
   *
   * @example
   * // Add 10 days to 1 September 2014:
   * const result = addDays(new Date(2014, 8, 1), 10)
   * //=> Thu Sep 11 2014 00:00:00
   */

  function addDays(dirtyDate, dirtyAmount) {
    requiredArgs(2, arguments);
    var date = toDate(dirtyDate);
    var amount = toInteger(dirtyAmount);

    if (isNaN(amount)) {
      return new Date(NaN);
    }

    if (!amount) {
      // If 0 days, no-op to avoid changing times in the hour before end of DST
      return date;
    }

    date.setDate(date.getDate() + amount);
    return date;
  }

  /**
   * @name addMonths
   * @category Month Helpers
   * @summary Add the specified number of months to the given date.
   *
   * @description
   * Add the specified number of months to the given date.
   *
   * @param {Date|Number} date - the date to be changed
   * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
   * @returns {Date} the new date with the months added
   * @throws {TypeError} 2 arguments required
   *
   * @example
   * // Add 5 months to 1 September 2014:
   * const result = addMonths(new Date(2014, 8, 1), 5)
   * //=> Sun Feb 01 2015 00:00:00
   */

  function addMonths(dirtyDate, dirtyAmount) {
    requiredArgs(2, arguments);
    var date = toDate(dirtyDate);
    var amount = toInteger(dirtyAmount);

    if (isNaN(amount)) {
      return new Date(NaN);
    }

    if (!amount) {
      // If 0 months, no-op to avoid changing times in the hour before end of DST
      return date;
    }

    var dayOfMonth = date.getDate(); // The JS Date object supports date math by accepting out-of-bounds values for
    // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
    // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
    // want except that dates will wrap around the end of a month, meaning that
    // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
    // we'll default to the end of the desired month by adding 1 to the desired
    // month and using a date of 0 to back up one day to the end of the desired
    // month.

    var endOfDesiredMonth = new Date(date.getTime());
    endOfDesiredMonth.setMonth(date.getMonth() + amount + 1, 0);
    var daysInMonth = endOfDesiredMonth.getDate();

    if (dayOfMonth >= daysInMonth) {
      // If we're already at the end of the month, then this is the correct date
      // and we're done.
      return endOfDesiredMonth;
    } else {
      // Otherwise, we now know that setting the original day-of-month value won't
      // cause an overflow, so set the desired day-of-month. Note that we can't
      // just set the date of `endOfDesiredMonth` because that object may have had
      // its time changed in the unusual case where where a DST transition was on
      // the last day of the month and its local time was in the hour skipped or
      // repeated next to a DST transition.  So we use `date` instead which is
      // guaranteed to still have the original time.
      date.setFullYear(endOfDesiredMonth.getFullYear(), endOfDesiredMonth.getMonth(), dayOfMonth);
      return date;
    }
  }

  /**
   * @name add
   * @category Common Helpers
   * @summary Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
   *
   * @description
   * Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
   *
   * @param {Date|Number} date - the date to be changed
   * @param {Duration} duration - the object with years, months, weeks, days, hours, minutes and seconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
   *
   * | Key            | Description                        |
   * |----------------|------------------------------------|
   * | years          | Amount of years to be added        |
   * | months         | Amount of months to be added       |
   * | weeks          | Amount of weeks to be added        |
   * | days           | Amount of days to be added         |
   * | hours          | Amount of hours to be added        |
   * | minutes        | Amount of minutes to be added      |
   * | seconds        | Amount of seconds to be added      |
   *
   * All values default to 0
   *
   * @returns {Date} the new date with the seconds added
   * @throws {TypeError} 2 arguments required
   *
   * @example
   * // Add the following duration to 1 September 2014, 10:19:50
   * const result = add(new Date(2014, 8, 1, 10, 19, 50), {
   *   years: 2,
   *   months: 9,
   *   weeks: 1,
   *   days: 7,
   *   hours: 5,
   *   minutes: 9,
   *   seconds: 30,
   * })
   * //=> Thu Jun 15 2017 15:29:20
   */
  function add(dirtyDate, duration) {
    requiredArgs(2, arguments);
    if (!duration || typeof duration !== 'object') return new Date(NaN);
    var years = duration.years ? toInteger(duration.years) : 0;
    var months = duration.months ? toInteger(duration.months) : 0;
    var weeks = duration.weeks ? toInteger(duration.weeks) : 0;
    var days = duration.days ? toInteger(duration.days) : 0;
    var hours = duration.hours ? toInteger(duration.hours) : 0;
    var minutes = duration.minutes ? toInteger(duration.minutes) : 0;
    var seconds = duration.seconds ? toInteger(duration.seconds) : 0; // Add years and months

    var date = toDate(dirtyDate);
    var dateWithMonths = months || years ? addMonths(date, months + years * 12) : date; // Add weeks and days

    var dateWithDays = days || weeks ? addDays(dateWithMonths, days + weeks * 7) : dateWithMonths; // Add days, hours, minutes and seconds

    var minutesToAdd = minutes + hours * 60;
    var secondsToAdd = seconds + minutesToAdd * 60;
    var msToAdd = secondsToAdd * 1000;
    var finalDate = new Date(dateWithDays.getTime() + msToAdd);
    return finalDate;
  }

  /**
   * @name addMilliseconds
   * @category Millisecond Helpers
   * @summary Add the specified number of milliseconds to the given date.
   *
   * @description
   * Add the specified number of milliseconds to the given date.
   *
   * @param {Date|Number} date - the date to be changed
   * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
   * @returns {Date} the new date with the milliseconds added
   * @throws {TypeError} 2 arguments required
   *
   * @example
   * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
   * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
   * //=> Thu Jul 10 2014 12:45:30.750
   */

  function addMilliseconds(dirtyDate, dirtyAmount) {
    requiredArgs(2, arguments);
    var timestamp = toDate(dirtyDate).getTime();
    var amount = toInteger(dirtyAmount);
    return new Date(timestamp + amount);
  }

  var MILLISECONDS_IN_MINUTE$1 = 60000;
  /**
   * @name addMinutes
   * @category Minute Helpers
   * @summary Add the specified number of minutes to the given date.
   *
   * @description
   * Add the specified number of minutes to the given date.
   *
   * @param {Date|Number} date - the date to be changed
   * @param {Number} amount - the amount of minutes to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
   * @returns {Date} the new date with the minutes added
   * @throws {TypeError} 2 arguments required
   *
   * @example
   * // Add 30 minutes to 10 July 2014 12:00:00:
   * const result = addMinutes(new Date(2014, 6, 10, 12, 0), 30)
   * //=> Thu Jul 10 2014 12:30:00
   */

  function addMinutes(dirtyDate, dirtyAmount) {
    requiredArgs(2, arguments);
    var amount = toInteger(dirtyAmount);
    return addMilliseconds(dirtyDate, amount * MILLISECONDS_IN_MINUTE$1);
  }

  /**
   * @name differenceInMilliseconds
   * @category Millisecond Helpers
   * @summary Get the number of milliseconds between the given dates.
   *
   * @description
   * Get the number of milliseconds between the given dates.
   *
   * @param {Date|Number} dateLeft - the later date
   * @param {Date|Number} dateRight - the earlier date
   * @returns {Number} the number of milliseconds
   * @throws {TypeError} 2 arguments required
   *
   * @example
   * // How many milliseconds are between
   * // 2 July 2014 12:30:20.600 and 2 July 2014 12:30:21.700?
   * const result = differenceInMilliseconds(
   *   new Date(2014, 6, 2, 12, 30, 21, 700),
   *   new Date(2014, 6, 2, 12, 30, 20, 600)
   * )
   * //=> 1100
   */

  function differenceInMilliseconds(dateLeft, dateRight) {
    requiredArgs(2, arguments);
    return toDate(dateLeft).getTime() - toDate(dateRight).getTime();
  }

  var roundingMap = {
    ceil: Math.ceil,
    round: Math.round,
    floor: Math.floor,
    trunc: function (value) {
      return value < 0 ? Math.ceil(value) : Math.floor(value);
    } // Math.trunc is not supported by IE

  };
  var defaultRoundingMethod = 'trunc';
  function getRoundingMethod(method) {
    return method ? roundingMap[method] : roundingMap[defaultRoundingMethod];
  }

  /**
   * @name roundToNearestMinutes
   * @category Minute Helpers
   * @summary Rounds the given date to the nearest minute
   *
   * @description
   * Rounds the given date to the nearest minute (or number of minutes).
   * Rounds up when the given date is exactly between the nearest round minutes.
   *
   * @param {Date|Number} date - the date to round
   * @param {Object} [options] - an object with options.
   * @param {Number} [options.nearestTo=1] - nearest number of minutes to round to. E.g. `15` to round to quarter hours.
   * @param {String} [options.roundingMethod='trunc'] - a rounding method (`ceil`, `floor`, `round` or `trunc`)
   * @returns {Date} the new date rounded to the closest minute
   * @throws {TypeError} 1 argument required
   * @throws {RangeError} `options.nearestTo` must be between 1 and 30
   *
   * @example
   * // Round 10 July 2014 12:12:34 to nearest minute:
   * const result = roundToNearestMinutes(new Date(2014, 6, 10, 12, 12, 34))
   * //=> Thu Jul 10 2014 12:13:00
   *
   * @example
   * // Round 10 July 2014 12:07:30 to nearest quarter hour:
   * const result = roundToNearestMinutes(new Date(2014, 6, 10, 12, 12, 34), { nearestTo: 15 })
   * // rounds up because given date is exactly between 12:00:00 and 12:15:00
   * //=> Thu Jul 10 2014 12:15:00
   */

  function roundToNearestMinutes(dirtyDate, options) {
    var _options$nearestTo;

    if (arguments.length < 1) {
      throw new TypeError('1 argument required, but only none provided present');
    }

    var nearestTo = toInteger((_options$nearestTo = options === null || options === void 0 ? void 0 : options.nearestTo) !== null && _options$nearestTo !== void 0 ? _options$nearestTo : 1);

    if (nearestTo < 1 || nearestTo > 30) {
      throw new RangeError('`options.nearestTo` must be between 1 and 30');
    }

    var date = toDate(dirtyDate);
    var seconds = date.getSeconds(); // relevant if nearestTo is 1, which is the default case

    var minutes = date.getMinutes() + seconds / 60;
    var roundingMethod = getRoundingMethod(options === null || options === void 0 ? void 0 : options.roundingMethod);
    var roundedMinutes = roundingMethod(minutes / nearestTo) * nearestTo;
    var remainderMinutes = minutes % nearestTo;
    var addedMinutes = Math.round(remainderMinutes / nearestTo) * nearestTo;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), roundedMinutes + addedMinutes);
  }

  var LibError = (function (_super) {
      __extends(LibError, _super);
      function LibError(message) {
          var _this = _super.call(this) || this;
          _this.message = "[react-headless-timeline]: ".concat(message);
          return _this;
      }
      return LibError;
  }(Error));

  var useTimelineProvider = function () {
      var value = React.useContext(TimelineCtx);
      if (!value) {
          throw new LibError('You forgot to wrap your timeline components with TimelineProvider!');
      }
      return value;
  };

  var useOffsetCalculator = function () {
      var timeline = useTimelineProvider();
      return function (startDate) {
          var timelineDatesDiff = Math.abs(differenceInMilliseconds(timeline.endDate, timeline.startDate));
          var startDatesDiff = Math.abs(differenceInMilliseconds(startDate, timeline.startDate));
          var offsetPercentage = (startDatesDiff * 100) / timelineDatesDiff;
          return "".concat(offsetPercentage, "%");
      };
  };

  var useSizeCalculator = function () {
      var timeline = useTimelineProvider();
      return function (_a) {
          var startDate = _a.startDate, endDate = _a.endDate;
          var timelineDatesDiff = Math.abs(differenceInMilliseconds(timeline.endDate, timeline.startDate));
          var eventDatesDiff = Math.abs(differenceInMilliseconds(endDate, startDate));
          var sizePercentage = (eventDatesDiff * 100) / timelineDatesDiff;
          return "".concat(sizePercentage, "%");
      };
  };

  var MILLISECONDS_IN_SECOND$1 = 1000;
  var CELLS_MIN = 2;
  var CELLS_DEFAULT = CELLS_MIN;
  function TimelineHeaders(_a) {
      var _b = _a.cells, cells = _b === void 0 ? CELLS_DEFAULT : _b, render = _a.render;
      var _c = useTimelineProvider(), startDate = _c.startDate, endDate = _c.endDate, direction = _c.direction;
      var calcOffset = useOffsetCalculator();
      var hasMoreCellsThanRequired = cells > CELLS_MIN;
      var isHorizontalTimeline = direction === 'horizontal';
      var cellSizeMs = Math.round((endDate.getTime() - startDate.getTime()) / (cells - 1));
      var headers = hasMoreCellsThanRequired
          ? new Array(cells).fill(null).reduce(function (dates, _, index) {
              dates.push(add(startDate, { seconds: (cellSizeMs / MILLISECONDS_IN_SECOND$1) * index }));
              return dates;
          }, [])
          : [startDate, endDate];
      var getHeaderStyles = function (header) { return (__assign({ position: 'absolute' }, (isHorizontalTimeline ? { left: calcOffset(header) } : { top: calcOffset(header) }))); };
      return render({ headers: headers, getHeaderStyles: getHeaderStyles });
  }

  function TimelineEvents(_a) {
      var render = _a.render;
      var direction = useTimelineProvider().direction;
      var calcOffset = useOffsetCalculator();
      var calcSize = useSizeCalculator();
      var isHorizontalTimeline = direction === 'horizontal';
      var getEventStyles = function (evt) { return (__assign({ position: 'absolute' }, (isHorizontalTimeline
          ? { width: calcSize(evt), left: calcOffset(evt.startDate) }
          : { height: calcSize(evt), top: calcOffset(evt.startDate) }))); };
      return render({ getEventStyles: getEventStyles });
  }

  var MILLISECONDS_IN_SECOND = 1000;
  var SECONDS_IN_MINUTE = 60;
  var MILLISECONDS_IN_MINUTE = SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;
  var UPDATE_INTERVAL_DEFAULT = 'minute';
  function TimelineCurrentTime(_a) {
      var _b = _a.updateInterval, updateInterval = _b === void 0 ? UPDATE_INTERVAL_DEFAULT : _b, render = _a.render;
      var _c = React.useState(new Date()), currentTime = _c[0], setCurrentTime = _c[1];
      var direction = useTimelineProvider().direction;
      var calcOffset = useOffsetCalculator();
      var isSecondInterval = updateInterval === 'second';
      var isHorizontalTimeline = direction === 'horizontal';
      React.useEffect(function () {
          var now = new Date();
          var nearestMinFromNow = roundToNearestMinutes(now);
          var nextFullMinuteFromNow = +nearestMinFromNow < +now ? addMinutes(nearestMinFromNow, 1) : nearestMinFromNow;
          var nowToNextFullMinuteInMilliseconds = Math.abs(differenceInMilliseconds(now, nextFullMinuteFromNow));
          var interval = isSecondInterval
              ? setInterval(function () { return setCurrentTime(new Date()); }, MILLISECONDS_IN_SECOND)
              : undefined;
          var timeout = isSecondInterval
              ? undefined
              : setTimeout(function () {
                  interval = setInterval(function () { return setCurrentTime(new Date()); }, MILLISECONDS_IN_MINUTE);
                  setCurrentTime(new Date());
              }, nowToNextFullMinuteInMilliseconds);
          return function () {
              clearTimeout(timeout);
              clearInterval(interval);
          };
      }, [updateInterval]);
      var styles = __assign({ position: 'absolute' }, (isHorizontalTimeline ? { left: calcOffset(currentTime) } : { top: calcOffset(currentTime) }));
      return render({ currentTime: currentTime, styles: styles });
  }

  function TimelineTimeIndicator(_a) {
      var render = _a.render;
      var direction = useTimelineProvider().direction;
      var calcOffset = useOffsetCalculator();
      var isHorizontalTimeline = direction === 'horizontal';
      var getIndicatorStyles = function (date) { return (__assign({ position: 'absolute' }, (isHorizontalTimeline ? { left: calcOffset(date) } : { top: calcOffset(date) }))); };
      return render({ getIndicatorStyles: getIndicatorStyles });
  }

  var index = {
      Provider: TimelineProvider,
      Headers: TimelineHeaders,
      Events: TimelineEvents,
      Indicators: {
          CurrentTime: TimelineCurrentTime,
          Time: TimelineTimeIndicator,
      },
  };

  exports.TimelineCurrentTime = TimelineCurrentTime;
  exports.TimelineEvents = TimelineEvents;
  exports.TimelineHeaders = TimelineHeaders;
  exports.TimelineProvider = TimelineProvider;
  exports.TimelineTimeIndicator = TimelineTimeIndicator;
  exports["default"] = index;

  Object.defineProperty(exports, '__esModule', { value: true });

}));
